// Metadata and structured bodies (§3.3): YAML 1.2.2's Core Schema restricted to JSON-compatible
// values. Duplicate keys, non-string keys, custom tags, anchors and aliases, non-finite numbers and
// a second document are errors; dates stay strings (the Core Schema infers no timestamps) and no
// constructor runs. JSON bodies are RFC 8259 JSON without duplicate keys.
import { isAlias, isPair, isScalar, parseAllDocuments, visit } from "yaml";

export type Loaded = { ok: true; value: unknown } | { ok: false; message: string };

const CORE_TAG = /^tag:yaml\.org,2002:(?:str|int|float|bool|null|seq|map)$/;

export function loadYaml(text: string): Loaded {
  const docs = parseAllDocuments(text, { schema: "core", uniqueKeys: true, merge: false, prettyErrors: false });
  const list = Array.isArray(docs) ? docs : [docs];
  if (list.length === 0) return { ok: true, value: null };
  if (list.length > 1) return { ok: false, message: "a second YAML document (a `---` line) is not allowed here" };
  const doc = list[0];
  if (doc.errors.length) return { ok: false, message: firstLine(doc.errors[0].message) };
  let problem: string | null = null;
  visit(doc, {
    Node(_key, node) {
      if (problem) return visit.BREAK;
      if (isAlias(node)) problem = "YAML aliases are not allowed";
      else if (node.anchor) problem = "YAML anchors are not allowed";
      else if (node.tag && !CORE_TAG.test(node.tag)) problem = `the YAML tag ${node.tag} is not allowed`;
      else if (isScalar(node) && typeof node.value === "number" && !Number.isFinite(node.value)) problem = "non-finite numbers are not allowed";
    },
    Pair(_key, pair) {
      if (problem) return visit.BREAK;
      if (isPair(pair) && !(isScalar(pair.key) && typeof pair.key.value === "string")) problem = "mapping keys must be strings";
    },
  });
  if (problem) return { ok: false, message: problem };
  const warning = doc.warnings.find((w) => w.code === "TAG_RESOLVE_FAILED");
  if (warning) return { ok: false, message: firstLine(warning.message) };
  return { ok: true, value: doc.toJS({ maxAliasCount: 0 }) };
}

export function loadJson(text: string): Loaded {
  let value: unknown;
  try { value = JSON.parse(text); } catch (e) { return { ok: false, message: firstLine((e as Error).message) }; }
  // (a number past what a double holds reads as Infinity, which canonical JSON, a digest, cannot write)
  if (!finite(value)) return { ok: false, message: "non-finite numbers are not allowed" };
  const dup = duplicateJsonKey(text);
  return dup === null ? { ok: true, value } : { ok: false, message: `the JSON key "${dup}" is repeated` };
}

/** Every number in a parsed JSON value is finite. */
function finite(v: unknown): boolean {
  if (typeof v === "number") return Number.isFinite(v);
  if (Array.isArray(v)) return v.every(finite);
  if (v && typeof v === "object") return Object.values(v).every(finite);
  return true;
}

/** The first key an object repeats in valid JSON text (JSON.parse keeps the last one silently). */
function duplicateJsonKey(text: string): string | null {
  let i = 0;
  const space = () => { while (i < text.length && " \t\r\n".includes(text[i])) i++; };
  const string = (): string => {
    const start = i++;
    while (text[i] !== '"') i += text[i] === "\\" ? 2 : 1;
    return JSON.parse(text.slice(start, ++i)) as string;
  };
  const value = (): string | null => {
    space();
    if (text[i] === "{") {
      i++; space();
      if (text[i] === "}") { i++; return null; }
      const keys = new Set<string>();
      for (;;) {
        space();
        const key = string();
        if (keys.has(key)) return key;
        keys.add(key);
        space(); i++; // the colon
        const inner = value();
        if (inner !== null) return inner;
        space();
        if (text[i++] === ",") continue;
        return null;
      }
    }
    if (text[i] === "[") {
      i++; space();
      if (text[i] === "]") { i++; return null; }
      for (;;) {
        const inner = value();
        if (inner !== null) return inner;
        space();
        if (text[i++] === ",") continue;
        return null;
      }
    }
    if (text[i] === '"') { string(); return null; }
    while (i < text.length && !" \t\r\n,]}".includes(text[i])) i++;
    return null;
  };
  return value();
}

/** RFC 4180 CSV (§7): a header of unique nonempty names, then records of the same width; cells stay strings. */
export function loadCsv(text: string): { ok: true; header: string[]; rows: string[][] } | { ok: false; message: string } {
  const records: string[][] = [];
  let record: string[] = [];
  let cell = "";
  let quoted = false;
  let i = 0;
  const src = text.replace(/\r\n?/g, "\n");
  while (i < src.length) {
    const c = src[i];
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') { cell += '"'; i += 2; continue; }
        quoted = false; i++; continue;
      }
      cell += c; i++; continue;
    }
    if (c === '"') {
      if (cell !== "") return { ok: false, message: `a quote inside an unquoted CSV field (record ${records.length + 1})` };
      quoted = true; i++; continue;
    }
    if (c === ",") { record.push(cell); cell = ""; i++; continue; }
    if (c === "\n") { record.push(cell); records.push(record); record = []; cell = ""; i++; continue; }
    cell += c; i++;
  }
  if (quoted) return { ok: false, message: "an unclosed quoted CSV field" };
  if (cell !== "" || record.length) { record.push(cell); records.push(record); }
  if (!records.length) return { ok: false, message: "a CSV body needs a header record" };
  const [header, ...rows] = records;
  if (header.some((h) => h === "")) return { ok: false, message: "CSV header names must be nonempty" };
  if (new Set(header).size !== header.length) return { ok: false, message: "CSV header names must be unique" };
  const bad = rows.findIndex((r) => r.length !== header.length);
  if (bad >= 0) return { ok: false, message: `CSV record ${bad + 2} has ${rows[bad].length} fields; the header has ${header.length}` };
  return { ok: true, header, rows };
}

function firstLine(s: string): string {
  return s.split("\n")[0].trim();
}
